import { zodResolver } from "@hookform/resolvers/zod";
import { MessageSource } from "@prisma/client";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { trpc } from "@calcom/trpc/react";
import {
  Button,
  Form,
  Label,
  Select,
  showToast,
  Switch,
  TextAreaField,
  TextField,
  Tooltip,
  MultiSelectCheckbox,
} from "@calcom/ui";

import { messageTemplateNames } from "@lib/constant";
import { parseMessageTemplate } from "@lib/practice/helper";
import { getDefaultPRTemplate, PRTemplateOptions } from "@lib/templates/paymentRequest/defaults";

import { InformationIcon, NewUsersIcon } from "@components/Icons";
import { useOfficeContext } from "@components/navbar";
import { MissingPatientPrompt } from "@components/patients/MissingPatientPrompt";
import PaymentSuccessDialog from "@components/patients/PaymentSuccessDialog";

type FormValues = {
  message: string;
  subject: string;
  showLedgerBreakdown: boolean;
};

type Props = {
  selectedPatientIds?: number[];
  officeId: number;
  setPaymentDialog: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function BatchPaymentRequest({ officeId, selectedPatientIds, setPaymentDialog }: Props) {
  const [loading, setLoading] = useState(false);
  const trpcUtil = trpc.useContext();

  const [search, setSearch] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [debouncedPatientIds, setDebouncedPatientIds] = useState<number[]>([]);

  const [patientIds, setPatientIds] = useState<number[]>(selectedPatientIds || []);
  const [template, setTemplate] = useState<any>(PRTemplateOptions[0]);

  const [paymentSuccessDialogOpen, setPaymentSuccessDialogOpen] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: {
      subject: "We're kindly requesting payment",
    },
    resolver: zodResolver(
      z.object({
        message: z.string().min(1, "Required"),
        subject: z.string().min(1, "Required"),
      })
    ),
  });

  const { register, formState, control, setValue, watch, getValues, reset } = form;

  const sendRequest = trpc.viewer.patients.sendBatchPaymentRequest.useMutation({
    onSuccess: (data) => {
      setLoading(false);
      setPaymentSuccessDialogOpen(true);
    },
    onError: (e) => {
      setLoading(false);
      showToast("Error occured", "error");
    },
  });

  const { data: patientOptions, isLoading } = trpc.viewer.patients.getNameList.useQuery({
    patientName: debouncedSearchQuery,
    officeId,
    patientIds: debouncedPatientIds,
  });

  const { office } = useOfficeContext();

  const { data: savedTemplates } = trpc.viewer.messageCustomize.getTemplates.useQuery({
    officeId,
    source: MessageSource.PAYMENT_REQUEST,
  });

  const handleSubmit = (data: FormValues) => {
    if (!patientIds.length) {
      showToast("Please select patient", "error");
      return;
    }
    setLoading(true);
    sendRequest.mutate({
      patientIds,
      officeId,
      template: template.value,
      message: data.message,
      subject: data.subject,
    });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(search);
    }, 800);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedPatientIds(patientIds);
    }, 800);
    return () => clearTimeout(timeoutId);
  }, [patientIds]);

  useEffect(() => {
    if (!template) return;

    const templateName = template.value;

    const saved = savedTemplates?.find((t) => t.templateName === templateName && t.type === "EMAIL");
    const defaultTpl = getDefaultPRTemplate(templateName);

    const rawContent = saved?.content || defaultTpl?.content || "";
    const rawSubject = saved?.subject || defaultTpl?.subject || "";

    const templateData: Record<string, string> = {
      patientName: "Patient Name",
      firstName: "Patient",
      officeName: office?.name || "",
      officePhone: office?.phone || "",
      officeEmail: office?.email || "",
      amount: "999.00",
      paymentLink: "(Payment link will pre-fill based on account selected)",
    };

    const parsedContent = parseMessageTemplate(rawContent, templateData);
    const parsedSubject = parseMessageTemplate(rawSubject, templateData);

    setValue("message", parsedContent);
    setValue("subject", parsedSubject);
  }, [template, savedTemplates, office]);

  return (
    <div>
      <Form className="w-full" form={form} handleSubmit={handleSubmit}>
        <div className="space-y-3 pb-6">
          <div className="mt-6 flex flex-col gap-5">
            <div id="batch-payment-request-account-select" className="relative">
              <Label>Accounts</Label>
              <div className="relative w-full">
                <MultiSelectCheckbox
                  placeholder="Search Account"
                  className="rounded-xl"
                  hasIcon={true}
                  options={patientOptions || []}
                  onInputChange={(e) => setSearch(e)}
                  isLoading={isLoading}
                  setSelected={(e) => setPatientIds(e.map((p: any) => p.value))}
                  selected={patientOptions?.filter((p) => patientIds.includes(p.value)) || []}
                />
                <div className="pointer-events-none absolute inset-0 left-2 right-auto flex items-center">
                  <NewUsersIcon />
                </div>
              </div>
              <MissingPatientPrompt
                officeId={officeId}
                visible={
                  debouncedSearchQuery.trim().length > 0 && !isLoading && (patientOptions?.length ?? 0) === 0
                }
                onPatientCreated={(patient) => {
                  setPatientIds((current) =>
                    current.includes(patient.patientId) ? current : [...current, patient.patientId]
                  );
                  setDebouncedPatientIds((current) =>
                    current.includes(patient.patientId) ? current : [...current, patient.patientId]
                  );
                  setSearch("");
                  setDebouncedSearchQuery("");
                }}
              />
            </div>
            <div id="batch-payment-request-templates-select" className="">
              <Label>Templates</Label>
              <div className="relative w-full">
                <Select
                  placeholder="Search Template"
                  className="rounded-xl"
                  value={PRTemplateOptions.find((p) => p.value === template?.value)}
                  options={PRTemplateOptions}
                  onChange={(option) => {
                    if (option) {
                      setTemplate(option);
                    }
                  }}
                />
              </div>
            </div>
            <div id="batch-payment-request-subject-input" className="">
              <Label className="flex items-center gap-2">
                Email Subject
                <Tooltip
                  content="The email subject will only appear when the message is sent as an email; it will not appear in a text message."
                  className="z-50 max-w-[500px]">
                  <span>
                    <InformationIcon />
                  </span>
                </Tooltip>
              </Label>
              <TextField
                label=""
                className="w-full"
                disabled={template?.value !== messageTemplateNames.PAYMENT_REQUEST_CUSTOM}
                {...register("subject")}
              />
            </div>
            <div id="batch-payment-request-message-input" className="flex flex-col gap-2 md:col-span-1">
              <div className="w-full">
                <TextAreaField
                  label="Message Content"
                  rows={template?.value !== messageTemplateNames.PAYMENT_REQUEST_CUSTOM ? 15 : 5}
                  disabled={template?.value !== messageTemplateNames.PAYMENT_REQUEST_CUSTOM}
                  readOnly={template?.value !== messageTemplateNames.PAYMENT_REQUEST_CUSTOM}
                  className="w-full disabled:bg-gray-50"
                  {...register("message")}
                />
              </div>
              <p className="text-sm text-gray-800">
                *Balance will be replaced with actual balance of the patient.
              </p>
              {template.value === messageTemplateNames.PAYMENT_REQUEST_CUSTOM && (
                <p className="text-sm text-gray-800">
                  *Payment link will pre-fill based on account selected at the end of the message
                </p>
              )}
            </div>
            <div id="batch-payment-request-ledger-breakdown-switch" className="hidden">
              <Controller
                name="showLedgerBreakdown"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="flex items-center justify-between">
                    <Label className="m-0 text-gray-600">Include Ledger Breakdown</Label>
                    <Switch label="" checked={value} onCheckedChange={onChange} />
                  </div>
                )}
              />
            </div>
            <Button
              id="batch-payment-request-submit-button"
              className="flex h-10 justify-center bg-black text-white hover:bg-black hover:text-white"
              type="submit"
              loadingTextClassName="text-black"
              loading={loading}>
              Send Payment Request
            </Button>
          </div>
        </div>
      </Form>
      <PaymentSuccessDialog
        open={paymentSuccessDialogOpen}
        title="Payment Request Sent"
        description="The payment request has been sent to the selected patients."
        source="Batch Payment Request"
        date={new Date().toISOString()}
        handler={setPaymentSuccessDialogOpen}
        onClose={() => {
          setPaymentSuccessDialogOpen(false);
          setPaymentDialog(false);
        }}
      />
    </div>
  );
}
